/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type IdToken, useAuth0, type User } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';

import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { type StenaPermissions } from '@sr/types';
import { useAccessToken } from '#lib/hooks/useAccessToken';
import { useTransactionId } from '../hooks/useTransactionId';
import { getApiUrl } from '../utils/environment';
import type { BasicProfile, Profile } from './Profile';

export const ProfileContext = createContext<ProfileValue | null>(null);

export type ProfileValue = {
  profile: Profile | null;
  loading: boolean;
  isAuthenticated: boolean;
  updateProfile: () => Promise<void>;
  error?: boolean;
  basicProfile: BasicProfile | null; //Needed to optimize app loading times
};

export type ProfileProviderProps = {
  children: ReactNode;
};
export const ProfileProvider = ({ children }: ProfileProviderProps) => {
  const { isAuthenticated, user, getIdTokenClaims, isLoading } = useAuth0();
  const { getAccessTokenSilently } = useAccessToken();
  const [loading, setLoading] = useState(isLoading);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [basicProfile, setBasicProfile] = useState<BasicProfile | null>(null);

  const [error, setError] = useState(false);

  const transactionId = useTransactionId();

  const fetchPermissions = useCallback(
    async (userId: string): Promise<StenaPermissions | null> => {
      const url = `${getApiUrl()}/usermanagement/user/${encodeURIComponent(userId)}/permissions`;
      const accessToken = await getAccessTokenSilently();
      const headers = new Headers();

      headers.set('Authorization', `Bearer ${accessToken}`);
      headers.set('X-Transaction-Id', transactionId);

      return fetch(url, { headers })
        .then(async (response) => {
          if (!response.ok) {
            const responseBody = await response
              .clone()
              .text()
              .catch(() => 'Unable to read response body');
            const error = new Error(`Failed to call ${url}`);

            Sentry.captureException(error, {
              extra: {
                url,
                status: response.status,
                statusText: response.statusText,
                method: 'GET',
                responseBody,
                headers: Object.fromEntries(headers.entries()),
              },
              tags: {
                fetch_error: true,
              },
            });

            throw error;
          }

          return response.json() as Promise<StenaPermissions>;
        })
        .catch((error: unknown) => {
          Sentry.captureException(error, {
            extra: {
              url,
              fetchOptions: { headers: Object.fromEntries(headers.entries()) },
              userIsOnline: navigator.onLine,
            },
            tags: {
              fetch_error: true,
              network_offline: !navigator.onLine,
            },
          });

          setError(true);

          return null;
        });
    },
    [getAccessTokenSilently, transactionId],
  );

  const parseProfileFromToken = useCallback(async () => {
    if (user) {
      let claims = await getIdTokenClaims();

      let profileFromClaims = mapToProfile(user, claims);

      if (!hasAggregatedPermissions(profileFromClaims)) {
        await getAccessTokenSilently({ cacheMode: 'off' });
        claims = await getIdTokenClaims();
        profileFromClaims = mapToProfile(user, claims);
      }

      if (!profileFromClaims.aggregatedPermissions) {
        setError(true);

        return;
      }

      setBasicProfile({
        userId: profileFromClaims.userId ?? '',
        email: profileFromClaims.email ?? '',
        language: profileFromClaims.language ?? 'en',
        fullName: profileFromClaims.fullName ?? '',

        aggregatedPermissions: profileFromClaims.aggregatedPermissions ?? {},
        rootPartnerIds: profileFromClaims.permissions?.rootPartnerIds ?? [],
      });

      if (profileFromClaims.permissions?.tooManyPartners) {
        const permissions = await fetchPermissions(user.sub ?? '');

        profileFromClaims.permissions = permissions;
      }

      setProfile({ ...profileFromClaims, language: profileFromClaims.language ?? 'en' });
      setLoading(false);
    }
  }, [user, getIdTokenClaims, getAccessTokenSilently, fetchPermissions]);

  useEffect(() => {
    if (isAuthenticated && !profile) {
      setLoading(true);
      void parseProfileFromToken();
    }
  }, [isAuthenticated, parseProfileFromToken, profile]);

  const updateProfile = useCallback(async () => {
    await getAccessTokenSilently({ cacheMode: 'off' });
    await parseProfileFromToken();

    if (!user?.sub) {
      return;
    }

    const permissions = await fetchPermissions(user.sub);

    if (permissions) {
      setProfile((currentProfile) =>
        currentProfile ? { ...currentProfile, permissions } : currentProfile,
      );
      setBasicProfile((currentProfile) =>
        currentProfile
          ? { ...currentProfile, rootPartnerIds: permissions.rootPartnerIds }
          : currentProfile,
      );
    }
  }, [getAccessTokenSilently, parseProfileFromToken, user?.sub, fetchPermissions]);

  const profileValue = useMemo(
    () => ({
      loading,
      profile,
      isAuthenticated,
      error,
      updateProfile,
      basicProfile,
    }),
    [loading, profile, isAuthenticated, error, updateProfile, basicProfile],
  );

  return <ProfileContext value={profileValue}>{children}</ProfileContext>;
};

const mapToProfile = (user: User, idTokenClaims?: IdToken): Profile => {
  const profile = {} as Profile;

  profile.email = idTokenClaims?.email ?? user.email;
  profile.firstName = idTokenClaims?.given_name ?? user.given_name;
  profile.lastName = idTokenClaims?.family_name ?? user.family_name;
  profile.lastLoggedIn = user.last_login;

  if (!idTokenClaims) {
    return profile;
  }

  profile.userId = idTokenClaims.sub;
  const namespace = 'https://stenarecycling.com/';

  profile.fullName = idTokenClaims[namespace + 'fullName'] ?? user.nickname;
  profile.language = idTokenClaims[namespace + 'language'];
  profile.phone = idTokenClaims[namespace + 'phone'];
  profile.permissions = idTokenClaims[namespace + 'permissions'];
  profile.industry = idTokenClaims[namespace + 'industry'];
  profile.seenRolesIndustrySurvey = idTokenClaims[namespace + 'seenRolesIndustrySurvey'];
  profile.aggregatedPermissions = idTokenClaims[namespace + 'aggregatedPermissions'];

  const roles = idTokenClaims[namespace + 'roles'];

  profile.roles = roles ?? [];

  return profile;
};

const hasAggregatedPermissions = (profile: Profile): boolean => {
  return !!profile.aggregatedPermissions && !!profile.aggregatedPermissions.markets;
};
